import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { useGetChatMediaQuery, useGetChatMessagesQuery, useGetChatThreadsQuery, useMarkChatReadMutation, useSendChatMessageMutation } from "~/api";
import { fetchWithAuth } from "~/common/fetch";
import { ChevronLeft, Maximize2, MessagesSquare, Paperclip, Send, X } from "~/common/redesign-v1/icons";
import { SearchInput } from "~/common/redesign-v1/ui";
import { useOptionalUser } from "~/common/user";
import { Loader } from "~/components/ui/loader";

import { clearChatComposeDraft, readChatComposeDraft, writeChatComposeDraft } from "./chat-compose-draft";
import { ChatFileDropZone } from "./chat-file-drop-zone";
import { chatFileKind, ChatFileThumbnail, ChatThreadTabButtons, resolveChatFileUrl } from "./chat-files";
import { ChatVideoExpandButton } from "./chat-image-preview";
import {
  channelTitleId,
  ChatDateSeparator,
  ChatThreadHeaderSubtitle,
  formatChatDateSeparator,
  formatChatThreadListTime,
  initialsFromPerson,
  MessageBubble,
  messageDayKey,
  ThreadListAvatar,
  threadPreviewText,
  type ThreadRow,
} from "./chat-page";
import { chatFileMatchesThreadSearch, chatMessageMatchesThreadSearch, normalizeChatThreadSearchQuery } from "./chat-thread-search";
import { filterVisibleChatThreads, isStaffChannelComposeLocked, sortChatThreadsByChannelOrder } from "./chat-thread-visibility";
import { useChatComposeAutogrow } from "./use-chat-compose-autogrow";
import { useChatMessagesAutoScroll } from "./use-chat-messages-scroll";
import { useChatThreadAllMessages } from "./use-chat-thread-all-messages";

type SupportMiniChatProps = {
  /** The parent sets false on close to reset navigation. */
  open: boolean;
  onClose: () => void;
};

export function SupportMiniChat({ open, onClose }: SupportMiniChatProps) {
  const intl = useIntl();
  const { data } = useGetChatThreadsQuery(undefined, {
    skip: !open,
    pollingInterval: open ? 5000 : 0,
  });
  const threadsRaw = data?.threads ?? [];
  const visibleThreads = useMemo(() => sortChatThreadsByChannelOrder(filterVisibleChatThreads(threadsRaw)), [threadsRaw]);

  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) setSelectedThreadId(null);
  }, [open]);

  const selectedThread = visibleThreads.find((t) => t.id === selectedThreadId) ?? null;

  return (
    <div className="tw-flex tw-size-full tw-min-h-0 tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-[#E5E5E5] tw-bg-white tw-shadow-md">
      <div className="tw-flex tw-shrink-0 tw-items-center tw-p-2">
        <div className="tw-min-w-0 tw-flex-1" />
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
          <Link
            to="/pages/chat"
            className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-2 tw-text-brand-black tw-no-underline tw-outline-none tw-transition-colors hover:tw-bg-gray-100 focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40 active:tw-scale-[0.85]"
            title={intl.formatMessage({ id: "chat.open.full" })}
            aria-label={intl.formatMessage({ id: "chat.open.full" })}
            onClick={() => onClose()}
          >
            <Maximize2 className="tw-size-4" />
          </Link>
          <button
            type="button"
            className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-2 tw-text-brand-black tw-outline-none tw-transition-colors hover:tw-bg-gray-100 focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40 active:tw-scale-[0.85]"
            onClick={onClose}
          >
            <X className="tw-size-4" aria-hidden />
            <span className="tw-sr-only">
              <FormattedMessage id="alert.close" />
            </span>
          </button>
        </div>
      </div>

      {selectedThread ? (
        <SupportMiniChatThread thread={selectedThread} onBack={() => setSelectedThreadId(null)} />
      ) : (
        <>
          <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-start tw-justify-center tw-border tw-border-[#E5E5E5] tw-bg-stone-200 tw-p-4">
            <div className="tw-flex tw-w-full tw-items-start tw-gap-3">
              <div className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-green tw-text-white">
                <MessagesSquare className="tw-size-4" />
              </div>
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                <p className="tw-m-0 tw-text-sm tw-font-medium tw-leading-5 tw-text-brand-black">
                  <FormattedMessage id="chat.support_team" />
                </p>
                <p className="tw-m-0 tw-text-xs tw-font-normal tw-leading-4 tw-text-brand-black">
                  <FormattedMessage id="chat.support_team.subtitle" />
                  <br aria-hidden />
                  <FormattedMessage id="chat.support_team.hours" />
                  <br aria-hidden />
                  <FormattedMessage id="chat.support_team.hours_schedule" />
                </p>
              </div>
            </div>
          </div>

          <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-2">
            <div className="tw-flex tw-flex-col tw-gap-2">
              {visibleThreads.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="tw-flex tw-w-full tw-shrink-0 tw-items-start tw-gap-4 tw-rounded-lg tw-border-0 tw-bg-white tw-p-4 tw-text-left tw-outline-none tw-transition-colors hover:tw-bg-gray-50 focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-brand-green/30"
                  onClick={() => setSelectedThreadId(t.id)}
                >
                  <ThreadListAvatar channel={t.channel} assignee={t.assignee ?? null} size="md" />
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-1">
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                      <p className="tw-m-0 tw-truncate tw-text-sm tw-font-medium tw-leading-5 tw-text-brand-black">
                        <FormattedMessage id={channelTitleId(t.channel)} />
                      </p>
                      <p className="tw-m-0 tw-line-clamp-2 tw-text-sm tw-font-normal tw-leading-5 tw-text-brand-black">{threadPreviewText(t, intl)}</p>
                    </div>
                    <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-justify-between tw-self-stretch">
                      <span className="tw-text-xs tw-font-normal tw-tabular-nums tw-leading-4 tw-text-[#737373]">{formatChatThreadListTime(t.last_message?.created_at ?? null, intl.locale)}</span>
                      {t.unread_count > 0 ? (
                        <span className="tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-green tw-text-[11px] tw-font-medium tw-leading-none tw-text-white">
                          {t.unread_count > 99 ? "99+" : t.unread_count}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SupportMiniChatThread({ thread, onBack }: { thread: ThreadRow; onBack: () => void }) {
  const intl = useIntl();
  const user = useOptionalUser();
  const threadId = thread.id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composeLocked = isStaffChannelComposeLocked(thread.channel, thread.assignee ?? null);
  const [activeTab, setActiveTab] = useState<"chat" | "media">("chat");
  const [threadSearchQuery, setThreadSearchQuery] = useState("");
  const threadSearchNormalized = normalizeChatThreadSearchQuery(threadSearchQuery);
  const threadSearchActive = threadSearchNormalized.length > 0;

  const { data, isLoading } = useGetChatMessagesQuery(
    { threadId, limit: 50 },
    {
      skip: activeTab !== "chat" || threadSearchActive,
      pollingInterval: activeTab === "chat" && !threadSearchActive ? 3500 : 0,
    },
  );
  const { messages: searchMessages, isLoading: isSearchMessagesLoading } = useChatThreadAllMessages(threadId, activeTab === "chat" && threadSearchActive);
  const mediaQuery = useGetChatMediaQuery({ threadId }, { skip: activeTab !== "media" && !threadSearchActive });
  const messages = threadSearchActive ? (searchMessages ?? []) : (data?.messages ?? []);
  const filteredMessages = useMemo(
    () => (threadSearchActive ? messages.filter((m) => chatMessageMatchesThreadSearch(m, threadSearchNormalized)) : messages),
    [messages, threadSearchActive, threadSearchNormalized],
  );
  const filteredMediaFiles = useMemo(() => {
    const files = mediaQuery.data?.files ?? [];
    return threadSearchActive ? files.filter((f) => chatFileMatchesThreadSearch(f, threadSearchNormalized)) : files;
  }, [mediaQuery.data?.files, threadSearchActive, threadSearchNormalized]);
  const showAssigneeRemovedNotice = composeLocked && (messages.length > 0 || Boolean(thread.last_message));
  const { listRef, onScroll, pinToBottom } = useChatMessagesAutoScroll(threadId, filteredMessages, activeTab === "chat" && !threadSearchActive);
  const [sendChatMessage, sendState] = useSendChatMessageMutation();
  const [markRead] = useMarkChatReadMutation();
  const doctorAccountId = user?.account_id ?? 0;
  const [draft, setDraft] = useState(() => readChatComposeDraft(doctorAccountId || undefined, threadId));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const composeTextareaRef = useChatComposeAutogrow(draft, 96);
  const [attachError, setAttachError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(readChatComposeDraft(doctorAccountId || undefined, threadId));
    setAttachError(null);
    setThreadSearchQuery("");
  }, [doctorAccountId, threadId]);

  useEffect(() => {
    if (!doctorAccountId) {
      return;
    }
    const timer = window.setTimeout(() => {
      writeChatComposeDraft(doctorAccountId, threadId, draftRef.current);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [doctorAccountId, threadId, draft]);

  useEffect(() => {
    if (!doctorAccountId) {
      return;
    }
    return () => {
      writeChatComposeDraft(doctorAccountId, threadId, draftRef.current);
    };
  }, [doctorAccountId, threadId]);
  const doctorAvatar = user?.avatar ?? undefined;
  const doctorInitials = user ? initialsFromPerson(user.last_name, user.first_name, user.middle_name) || "?" : "?";

  async function onSend() {
    if (composeLocked) return;
    const text = draft.trim();
    if (!text) return;
    await sendChatMessage({ threadId, text }).unwrap();
    clearChatComposeDraft(doctorAccountId || undefined, threadId);
    setDraft("");
    pinToBottom();
  }

  async function attachFiles(files: File[]) {
    if (composeLocked) return;
    setAttachError(null);
    if (files.length === 0) return;
    const list = files.slice(0, 10);
    const form = new FormData();
    for (const f of list) form.append("files", f);

    const res = await fetchWithAuth(`${process.env.API}/chat/threads/${threadId}/uploads`, {
      method: "POST",
      body: form,
    });
    if (!res.ok) {
      let msg = intl.formatMessage({ id: "chat.upload.error" });
      try {
        const err = (await res.json()) as { message?: string; description?: string };
        msg = err.description || err.message || msg;
      } catch {
        /* ignore */
      }
      setAttachError(msg);
      return;
    }
    const payload = (await res.json()) as { files?: { id: number }[] };
    const fileIds = (payload.files ?? []).map((f) => f.id);
    if (fileIds.length) {
      await sendChatMessage({ threadId, fileIds }).unwrap();
      pinToBottom();
    }
    setActiveTab("chat");
  }

  async function onAttach(files: FileList | null) {
    if (!files || files.length === 0) return;
    await attachFiles(Array.from(files));
  }

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    void markRead({ threadId, lastMessageId: last.id });
  }, [messages, markRead, threadId]);

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      <div className={clsx("tw-flex tw-shrink-0 tw-flex-col tw-justify-between tw-border-b tw-border-[#E8EAED] tw-bg-white", activeTab !== "media" && "tw-h-[150px]")}>
        {activeTab === "media" ? (
          <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-2.5">
            <div className="tw-flex tw-items-center tw-gap-3">
              <button
                type="button"
                onClick={() => setActiveTab("chat")}
                aria-label={intl.formatMessage({ id: "reminder.back" })}
                className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-black tw-outline-none hover:tw-opacity-70 focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40"
              >
                {/* Remove native button padding so the 24px icon does not shrink. */}
                <ChevronLeft className="tw-size-6 tw-shrink-0" />
              </button>
              <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-text-sm tw-font-semibold tw-text-brand-black">
                  <FormattedMessage id="chat.files.title" defaultMessage="Chat files" />
                </div>
                <div className="tw-text-xs tw-font-normal tw-text-brand-black">
                  <FormattedMessage id="chat.files.count" defaultMessage="{count, plural, one {# file} other {# files}}" values={{ count: filteredMediaFiles.length }} />
                </div>
              </div>
            </div>
            <div className="tw-pl-8">
              <SearchInput
                compact
                value={threadSearchQuery}
                onChange={(e) => setThreadSearchQuery(e.currentTarget.value)}
                onClear={() => setThreadSearchQuery("")}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setThreadSearchQuery("");
                }}
                placeholder={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
                clearAriaLabel={intl.formatMessage({ id: "chat.thread.search.clear" })}
                aria-label={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
              />
            </div>
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-2.5">
            <div className="tw-flex tw-items-center tw-gap-3">
              <button
                type="button"
                onClick={onBack}
                aria-label={intl.formatMessage({ id: "chat.mini.back" })}
                className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-black tw-outline-none hover:tw-opacity-70 focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40"
              >
                <ChevronLeft className="tw-size-6" aria-hidden />
              </button>
              <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-whitespace-normal tw-break-words tw-text-sm tw-font-semibold tw-leading-tight tw-text-brand-black">
                  <FormattedMessage id={channelTitleId(thread.channel)} />
                </div>
                <ChatThreadHeaderSubtitle
                  className="tw-mt-0.5"
                  channel={thread.channel}
                  assignee={thread.assignee ?? null}
                  locale={intl.locale}
                  subtitleClassName="tw-text-xs tw-font-medium tw-leading-snug tw-text-brand-black"
                  descriptionClassName="tw-text-xs tw-font-normal tw-leading-snug tw-text-brand-black"
                  hideDescription={threadSearchActive}
                />
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-pl-8">
              <div className="tw-min-w-0 tw-flex-1">
                <SearchInput
                  compact
                  value={threadSearchQuery}
                  onChange={(e) => setThreadSearchQuery(e.currentTarget.value)}
                  onClear={() => setThreadSearchQuery("")}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setThreadSearchQuery("");
                  }}
                  placeholder={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
                  clearAriaLabel={intl.formatMessage({ id: "chat.thread.search.clear" })}
                  aria-label={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
                />
              </div>
              <div className="tw-ml-auto tw-flex tw-shrink-0">
                <ChatThreadTabButtons activeTab={activeTab} onActiveTabChange={setActiveTab} />
              </div>
            </div>
          </div>
        )}
        <div className="tw-h-px tw-w-full tw-shrink-0 tw-bg-[#E1E4ED]" aria-hidden />
      </div>

      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
        {activeTab === "media" ? (
          <ChatFileDropZone className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col" compact disabled={composeLocked} onDropFiles={(files) => void attachFiles(files)}>
            <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-bg-[#F5F5F5] tw-p-3">
              {mediaQuery.isLoading ? <Loader /> : null}
              {threadSearchActive && !mediaQuery.isLoading && filteredMediaFiles.length === 0 ? (
                <p className="tw-m-0 tw-text-center tw-text-xs tw-text-[#6B7280]">
                  <FormattedMessage id="chat.thread.search.empty" />
                </p>
              ) : null}
              {!threadSearchActive && !mediaQuery.isLoading && filteredMediaFiles.length === 0 ? (
                <p className="tw-m-0 tw-text-center tw-text-xs tw-text-[#6B7280]">
                  <FormattedMessage id="chat.files.empty" defaultMessage="No files yet" />
                </p>
              ) : null}
              <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                {filteredMediaFiles.map((f) => {
                  const kind = chatFileKind(f.mime_type, f.file_name);
                  const url = resolveChatFileUrl(f.get_url);
                  const cardCls = "tw-flex tw-h-[8.5rem] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-[#E4E4E4] tw-bg-white";
                  const nameCls = "tw-block tw-min-w-0 tw-shrink-0 tw-truncate tw-px-1.5 tw-py-1 tw-text-[10px] tw-text-brand-black hover:tw-underline";
                  if (kind === "video" && url) {
                    return (
                      <div key={f.id} className={cardCls}>
                        <div className="tw-relative tw-min-h-0 tw-w-full tw-flex-1">
                          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- chat file attachment */}
                          <video className="tw-size-full tw-bg-black tw-object-cover" controls preload="metadata" playsInline src={url} />
                          <ChatVideoExpandButton src={url} alt={f.file_name} />
                        </div>
                        <a href={url} target="_blank" rel="noreferrer" className={nameCls}>
                          {f.file_name}
                        </a>
                      </div>
                    );
                  }
                  return (
                    <div key={f.id} className={cardCls}>
                      <div className="tw-min-h-0 tw-w-full tw-flex-1 tw-overflow-hidden">
                        <ChatFileThumbnail fileName={f.file_name} mimeType={f.mime_type} url={url} variant="grid" iconClassName="tw-size-6" />
                      </div>
                      <a href={url} target="_blank" rel="noreferrer" className={nameCls}>
                        {f.file_name}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </ChatFileDropZone>
        ) : (
          <>
            <ChatFileDropZone className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col" compact disabled={composeLocked} onDropFiles={(files) => void attachFiles(files)}>
              <div ref={listRef} onScroll={onScroll} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-bg-[#F5F5F5] tw-px-3 tw-py-4">
                {(isLoading || isSearchMessagesLoading) && filteredMessages.length === 0 ? <Loader /> : null}
                {threadSearchActive && !isLoading && !isSearchMessagesLoading && filteredMessages.length === 0 ? (
                  <p className="tw-m-0 tw-text-center tw-text-xs tw-text-[#6B7280]">
                    <FormattedMessage id="chat.thread.search.empty" />
                  </p>
                ) : null}
                {filteredMessages.map((m, index) => {
                  const dayKey = messageDayKey(m.created_at);
                  const prevDayKey = index > 0 ? messageDayKey(filteredMessages[index - 1]?.created_at) : null;
                  const showDateSep = Boolean(dayKey && dayKey !== prevDayKey);
                  return (
                    <React.Fragment key={m.id}>
                      {showDateSep ? <ChatDateSeparator label={formatChatDateSeparator(m.created_at, intl.locale)} /> : null}
                      <MessageBubble
                        message={m}
                        channel={thread.channel}
                        assignee={thread.assignee ?? null}
                        locale={intl.locale}
                        doctorAccountId={doctorAccountId}
                        doctorAvatar={doctorAvatar}
                        doctorInitials={doctorInitials}
                        tone="default"
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </ChatFileDropZone>

            <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2 tw-border-t tw-border-[#E1E4ED] tw-bg-white tw-p-4">
              {showAssigneeRemovedNotice ? (
                <div
                  className="tw-whitespace-pre-line tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-[11px] tw-font-medium tw-leading-snug tw-text-amber-950"
                  role="status"
                >
                  <FormattedMessage id={thread.channel === "personal_manager" ? "chat.readonly.no_assignee_notice_manager" : "chat.readonly.no_assignee_notice"} />
                </div>
              ) : null}
              {attachError ? <div className="tw-text-xs tw-text-red-600">{attachError}</div> : null}
              <div className="tw-flex tw-items-end tw-gap-2 tw-overflow-hidden tw-rounded-lg tw-bg-white">
                <input ref={fileInputRef} type="file" className="tw-sr-only" multiple onChange={(e) => void onAttach(e.target.files)} />
                <button
                  type="button"
                  className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-[#9CA3AF] tw-outline-none tw-transition-colors hover:tw-text-brand-black focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/30 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                  title={intl.formatMessage({ id: "chat.attach" })}
                  aria-label={intl.formatMessage({ id: "chat.attach" })}
                  disabled={composeLocked}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="tw-size-5 tw-shrink-0" />
                </button>
                <textarea
                  ref={composeTextareaRef}
                  rows={1}
                  className="tw-placeholder:text-black/35 tw-max-h-24 tw-min-h-8 tw-min-w-0 tw-flex-1 tw-resize-none tw-overflow-y-auto tw-rounded-lg tw-border tw-border-[#E1E4ED] tw-bg-white tw-px-3 tw-py-1.5 tw-text-[13px] tw-leading-[18px] tw-text-black/85 tw-outline-none focus:tw-border-[#C5CAD6] disabled:tw-cursor-not-allowed disabled:tw-bg-[#F3F4F6] disabled:tw-text-black/40"
                  placeholder={intl.formatMessage({ id: "chat.message.placeholder" })}
                  value={draft}
                  disabled={composeLocked}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void onSend();
                    }
                    // Shift+Enter falls through to the textarea's native newline insertion.
                  }}
                />
                <button
                  type="button"
                  className="chat-compose-send-btn tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-[#171717] tw-shadow-none tw-outline-none tw-ring-0 tw-ring-offset-0 focus:tw-outline-none focus:tw-ring-0 focus-visible:tw-outline-none focus-visible:tw-ring-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                  style={{ outline: "none", border: "none", boxShadow: "none" }}
                  aria-label={intl.formatMessage({ id: "chat.send" })}
                  disabled={composeLocked || sendState.isLoading || !draft.trim()}
                  onClick={() => void onSend()}
                >
                  <Send className="tw-size-4 tw-shrink-0 tw-text-white" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
